import { ArrowDown, ArrowUp, Clock, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DeliveryStat } from '@/lib/messaging-data'

const toneClass: Record<DeliveryStat['tone'], string> = {
  healthy: 'text-healthy',
  degraded: 'text-degraded',
  critical: 'text-critical',
  // Deliverability is a ratio, not a health verdict — blue keeps it from being
  // read as "good" or "bad" at a glance the way the other three are.
  info: 'text-info',
  neutral: 'text-fg',
}

/**
 * A messaging counter with its 24-hour movement.
 *
 * The arrow's color follows meaning, not direction: rising sends are good and
 * rising spam reports are not, so tiles flagged `inverted` flip the palette. An
 * arrow that is always green when it points up would tell the reader the wrong
 * thing on three of these four tiles.
 */
export function DeliveryStatTile({ stat }: { stat: DeliveryStat }) {
  const { label, value, tone, change, caption, inverted, highlight, hint } = stat
  const Arrow = change?.direction === 'up' ? ArrowUp : ArrowDown
  const good = change?.direction === (inverted ? 'down' : 'up')
  const CaptionIcon = caption?.icon === 'clock' ? Clock : TriangleAlert

  return (
    <div
      className={cn(
        'bg-surface rounded-lg border p-5',
        highlight ? 'border-critical/50 border-dashed' : 'border-line',
      )}
    >
      {/* The payload's notes explain how the figure was counted — a caveat worth
          having, but not worth the layout it would take to print. */}
      <p
        title={hint}
        className={cn(
          'text-fg-muted font-mono text-[11px] font-semibold tracking-[0.08em] uppercase',
          hint && 'cursor-help',
        )}
      >
        {label}
      </p>
      <p className={cn('mt-4 font-mono text-4xl leading-none font-bold', toneClass[tone])}>
        {value}
      </p>
      {change && (
        <p
          className={cn(
            'mt-3 flex items-center gap-1 font-mono text-[13px]',
            good ? 'text-healthy' : 'text-critical',
          )}
        >
          <Arrow className="size-3.5" />
          {change.value}
          <span className="sr-only">
            {change.direction === 'up' ? 'up' : 'down'} against the previous 24 hours
          </span>
        </p>
      )}

      {caption && (
        <p
          className={cn(
            'mt-3 flex items-center gap-1.5 text-[13px]',
            // A warning caption carries the tile's own alarm; a neutral one
            // ("Stable") is just context and stays quiet.
            caption.icon === 'warning' ? 'text-critical' : 'text-fg-muted',
          )}
        >
          <CaptionIcon className="size-3.5" />
          {caption.text}
        </p>
      )}
    </div>
  )
}
